import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * A panel that slides in from the right, over the workspace.
 *
 * The dial workspace had no overlay of its own — the one modal in it (the centre-logo editor) is hand-rolled
 * inline. A review schedule outgrew the inline form it used to be: a date, a revert target, a reminder
 * horizon, a repeat and a readout of who hears about it do not belong in a row tucked under a status
 * picker, where every extra control pushed the item's own content further down the card.
 *
 * A sheet rather than a centred dialog because the thing being scheduled stays visible beside it — you are
 * setting a date *for that item*, and losing sight of which one behind a modal scrim is exactly the mistake
 * a full-width dialog invites.
 */
export function Sheet({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** Pinned to the bottom, out of the scroll — a Save you have to scroll to find is a Save people miss. */
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  // Read through a ref so the listener below has stable dependencies. Callers pass an inline arrow for
  // onClose, which is a new identity on every render — depending on it directly re-runs the effect on
  // every keystroke, and the focus() call in it then STEALS FOCUS BACK from whatever the user is typing
  // into. The symptom is a text field that silently accepts only its first character.
  const close = useRef(onClose);
  close.current = onClose;

  // Focus once, on mount: a keyboard user should not be left behind on the page the sheet covers.
  useEffect(() => {
    panel.current?.focus();
  }, []);

  // Escape closes. Bound to the panel rather than window so a sheet opened inside another overlay closes
  // only the innermost. Tab wraps within the panel: a modal sheet that let focus walk out into the page it
  // covers would leave a keyboard user typing into a form they can't see.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== "Tab" || !panel.current) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const node = panel.current;
    node?.addEventListener("keydown", onKey);
    return () => node?.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.32)", zIndex: 60 }}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(460px, 100vw)",
          zIndex: 61,
          display: "flex",
          flexDirection: "column",
          background: "var(--surface)",
          borderLeft: "var(--border-w) solid var(--border)",
          boxShadow: "var(--shadow-lg)",
          outline: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "16px 18px", borderBottom: "var(--border-w) solid var(--border)" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--text)" }}>{title}</div>
            {subtitle && (
              <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="kd-focusable"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
              width: 30, height: 30, borderRadius: "var(--radius-sm)", cursor: "pointer",
              border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)",
            }}
          >
            <Icon name="x" size={15} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
          {children}
        </div>

        {footer && (
          <div style={{ padding: "12px 18px", borderTop: "var(--border-w) solid var(--border)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
